import React, { useState, useEffect } from "react";

import cross from "assets/images/adduser/cross.svg";
import crossDark from "assets/images/adduser/crossDark.svg";
import list from "assets/images/dropdown/chevronleft.svg";
import downArrow from "assets/images/dropdown/downArrowDark.svg";
import successpic from "assets/images/uploadfiles/uploadSuccess.svg";
import failed from "assets/images/uploadfiles/failed.svg";
import folder from "assets/images/homepage/ySafe/tableIcons/folder.svg";
import "./styles.scss";
import useTheme from "hooks/observer";
import useScreenMobile from "hooks/useScreen";
import { getFileIcon } from "containers/Homepage/Ysafe/UploadFileContent";
import CircularProgress from "./CircularProgress";
import { cancelRequest } from "@induz/sdk";
const TaskMonitor = ({
    taskManager,
    TaskMonitorVisible,
    setTaskMonitorVisible
}
) => {
    const normalizeProgress = (value) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) return 0;
        return Math.max(0, Math.min(100, Math.floor(numeric)));
    };
    const [showList, setShowList] = useState(true);
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState("running"); // "running" or "history"
    const [tasks, setTasks] = useState([]);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        if (TaskMonitorVisible) {
            setShowList(true);
        }
    }, [TaskMonitorVisible]);

    useEffect(() => {
        const interval = setInterval(() => {
            try {
                // Get history
                const hist = taskManager.getCompletedTasks();
                const historyList = hist.map((obj) => ({
                    id: obj.getId(),
                    description: obj.getDescription(),
                    progress: obj.getProgress(),
                    status: obj.getStatus(),
                    name: obj.getName()
                }));

                // Get running tasks
                const rt = taskManager.getRunningTasks();

                const tasksList = rt.map((obj) => ({
                    id: obj.getId(),
                    description: obj.getDescription(),
                    progress: obj.getProgress(),
                    status: obj.getStatus(),
                    name: obj.getName(),
                    detail: obj.taskDescription || ""
                }));
                setHistory(historyList);
                setTasks(tasksList);
            } catch (e) {
                console.error("WASM error:", e);
            }
        }, 500);

        return () => clearInterval(interval);
    }, []);
    const cancelHandler = async (task) => {
        setTasks(prevTasks =>
            prevTasks.map(t =>
                t.id === task.id ? { ...t, status: 3 } : t
            )
        );
        try {
            taskManager.cancelTask(task.id, 3);
            await cancelRequest(task.id);
        } catch (error) {
            console.warn("Task could not be cancelled natively, it may have already completed or failed.", error);
        }
    };

    const getOperationEmoji = (task) => {
        // Check description first (for uploading/downloading)
        const desc = (task.description || "").toLowerCase();
        const name = (task.name || "").toLowerCase();

        if (desc.includes("download") || name.includes("download") || name.includes("d...") || name.includes("d…")) return "⬇️";
        if (desc.includes("upload") || name.includes("upload") || name.includes("u...") || name.includes("u…")) return "⬆️";
        if (desc.includes("copy") || name.includes("copy") || name.includes("c...") || name.includes("c…")) return "📄";
        if (desc.includes("delete") || desc.includes("trash") || desc.includes("tras") || name.includes("delet") || name.includes("trash") || name.includes("tras") || name.includes("d...") || name.includes("d…")) return "🗑️";
        if (
            desc.includes("move") ||
            name.includes("move") ||
            name.includes("moved") ||
            desc.includes("moved") ||
            desc.includes("➡") ||
            name.includes("➡") ||
            name.includes("m...") ||
            name.includes("m…")
        ) return "➡️";
        if (desc.includes("restore") || name.includes("restore") || name.includes("res...") || name.includes("res…")) return "🔄";
        if (desc.includes("creating") || name.includes("creating")) return "➕";
        return "";
    };

    const removeOperationText = (task) => {
        const text = task.name || "";
        const lowerText = text.toLowerCase();
        if (lowerText.startsWith("downloading ")) return text.substring(12);
        if (lowerText.startsWith("uploading ")) return text.substring(10);
        if (lowerText.startsWith("copying ")) return text.substring(8);
        if (lowerText.startsWith("moving ")) return text.substring(7);
        if (lowerText.startsWith("creating ")) return text.substring(9);
        if (lowerText.startsWith("deleting ")) return text.substring(9);
        // For uploading/downloading, the description contains the operation word, so just return the name
        return text;
    };

    const getFileNameFromTask = (task) => {
        return removeOperationText(task);
    };

    const truncateFileName = (fileName, maxLength = 30) => {
        if (!fileName || fileName.length <= maxLength) {
            return fileName;
        }
        return fileName.substring(0, maxLength) + "...";
    };
    const isFolderOperation = (task) => {
        const desc = (task.description || "").toLowerCase();
        const name = (task.name || "").toLowerCase();

        // 1. Explicit creating check
        if (desc.includes("creating") || name.includes("creating")) return true;

        // 2. Check for summary patterns (e.g., "1 folder", "10 file(s)")
        // These are strong indicators for bulk operations
        const hasFolderWord = /\b\d+\s+folders?\b/.test(name) || /\b\d+\s+folders?\b/.test(desc);
        const hasFileWord = /\b\d+\s+files?\b/.test(name) || /\b\d+\s+files?\b/.test(desc);

        if (hasFolderWord) return true;
        if (hasFileWord) return false;

        // 3. Fallback to extension check for individual items
        const itemName = removeOperationText(task);
        const lastDotIndex = itemName.lastIndexOf(".");

        // If it has a dot and no space after it, it's likely a file
        if (lastDotIndex > 0 && lastDotIndex < itemName.length - 1) {
            const ext = itemName.substring(lastDotIndex + 1);
            if (!ext.includes(" ")) {
                return false; // Likely a file
            }
        }

        // 4. If no extension, look for keywords as whole words
        // This catches "Moving folder1" or "Deleting files"
        if (/\bfolders?\b/.test(name) || /\bfolders?\b/.test(desc)) return true;
        if (/\bfiles?\b/.test(name) || /\bfiles?\b/.test(desc)) return false;

        // 5. Default for items without dots: assume folder if it doesn't mention "items"
        if (name.includes("item(s)") || name.includes("items") || desc.includes("item(s)")) return false;
        return true;
    };

    const isMobile = useScreenMobile({ size: 992 });
    return (
        <div
            className={TaskMonitorVisible ? "dropDownContainer" : "hideDropDownContainer"}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <div className="header">
                <p className="text">Operation Panel</p>
                <div className="icons">
                    <img
                        src={theme == "light" ? list : downArrow}
                        alt="show/hide list"
                        className={showList ? "" : "hide"}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowList((pre) => !pre);
                            if (isMobile) {
                                // TODO:: Do something here
                            }
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    />
                    <img
                        src={theme == "light" ? cross : crossDark}
                        alt="close"
                        onClick={(e) => {
                            e.stopPropagation();
                            setTaskMonitorVisible(false);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    />
                </div>
            </div>
            <div className={showList ? "tab-nav" : "hide-tab-nav"}>
                <button
                    className={activeTab === "running" ? "active" : ""}
                    onClick={() => setActiveTab("running")}>
                    Running Tasks
                </button>
                <button
                    className={activeTab === "history" ? "active" : ""}
                    onClick={() => setActiveTab("history")}>
                    History
                </button>
            </div>

            <div className={showList ? "files" : "hideFiles"}>
                {activeTab === "running" && tasks?.length > 0 ? (
                    tasks.map((task, index) => {
                        const fileName = getFileNameFromTask(task);
                        const isFolder = isFolderOperation(task);
                        const displayFileName = truncateFileName(fileName);
                        const fullFileName = getOperationEmoji(task) + (getOperationEmoji(task) ? " " : "") + fileName;
                        const displayText = getOperationEmoji(task) + (getOperationEmoji(task) ? " " : "") + displayFileName;
                        return (
                            <div className="file" key={index}>
                                <div className="content">
                                    {task.status !== 3 && (isFolder ? (
                                        <img
                                            src={folder}
                                            alt="folder"
                                            style={{ width: "20px", height: "20px" }}
                                        />
                                    ) : (
                                        <img
                                            src={getFileIcon(fileName)}
                                            alt="file"
                                            style={{ width: "20px", height: "20px" }}
                                        />
                                    ))}
                                    <div className="nameBytesWrapper">
                                        <p className="fileName" title={fileName.length > 35 ? fullFileName : undefined}>
                                            {displayText}
                                        </p>
                                        {task.detail && (
                                            <p className="taskDetail">
                                                {task.detail}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {
                                    task.status === 3 ?
                                        <div className="progresscircle">
                                            <CircularProgress cancelled={true} />
                                        </div>
                                        :
                                        <div className="progresscircle" onClick={() => { cancelHandler(task); }}>
                                            <CircularProgress percentage={normalizeProgress(task.progress)} />
                                        </div>
                                }
                            </div>
                        );
                    })
                ) : (
                    activeTab === "running" && <div className="file">
                        <div className="content">
                            <div className="nameBytesWrapper">
                                <div className="no-tasks">
                                    <p>No operation is being performed</p>
                                </div>
                            </div>
                        </div>
                        <div>
                        </div>
                    </div>
                )}

                {activeTab === "history" && history.length > 0 ? [...history].reverse().map((task, index) => {
                    const fileName = getFileNameFromTask(task);
                    const isFolder = isFolderOperation(task);
                    const displayFileName = truncateFileName(fileName);
                    const fullFileName = getOperationEmoji(task) + (getOperationEmoji(task) ? " " : "") + fileName;
                    const displayText = getOperationEmoji(task) + (getOperationEmoji(task) ? " " : "") + displayFileName;
                    return (
                        <div className="file" key={index}>
                            <div className="content">
                                {task.status !== 3 && (isFolder ? (
                                    <img
                                        src={folder}
                                        alt="folder"
                                        style={{ width: "20px", height: "20px", marginRight: "8px" }}
                                    />
                                ) : (
                                    <img
                                        src={getFileIcon(fileName)}
                                        alt="file"
                                        style={{ width: "20px", height: "20px", marginRight: "8px" }}
                                    />
                                ))}
                                <div className="nameBytesWrapper">
                                    <p className="fileName" title={fileName.length > 35 ? fullFileName : undefined}>
                                        {displayText}
                                    </p>
                                </div>
                            </div>
                            {task.status === 3 ? (
                                <div className="success">
                                    <img src={failed} style={{ scale: "1.0" }} alt="cancelled" />
                                </div>
                            ) : task.status === 1 ? (
                                <div className="success">
                                    <img src={successpic} alt="success" />
                                </div>
                            ) : (
                                <div className="success">
                                    <img src={failed} style={{ scale: "1.0" }} alt="failed" />
                                </div>
                            )}
                        </div>
                    );
                }) :
                    (activeTab === "history" && <div className="file">
                        <div className="content">
                            <div className="nameBytesWrapper">
                                <div className="no-tasks">
                                    <p>This contains this session history</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    )
                }
            </div>
        </div>
    );
};

export default TaskMonitor;